import { FlyingCoinsRef } from "@components/utils/FlyingCoins/FlyingCoins";
import SpinningNumber, { SpinningNumberRef } from "@components/utils/SpinningNumber";
import useAuthSession from "@hooks/useAuthSession";
import { getThemeAsset } from "@utils/theme";
import { AnimatePresence, motion } from "framer-motion";
import { PropsWithClassName, cn } from "kz-ui-sdk";
import { AriaAttributes, useCallback, useEffect, useRef, useState } from "react";
import {
  SkinnerBoxActions,
  SkinnerBoxBackground,
  SkinnerBoxCoins,
  SkinnerBoxContent,
  SkinnerBoxSparkles,
} from "./components";
import { useSkinnerBoxState } from "./hooks/useSkinnerBoxState";
import { ANIMATION_CONFIG, ANIMATION_STATE, SKINNER_BOX_CONFIG, SKINNER_BOX_STATE, containerVariants } from "./utils";

interface SkinnerBoxFABProps extends PropsWithClassName, AriaAttributes {
  initialDelay?: number;
  onAnimationStart?: () => void;
  zIndex?: number;
  claimableAmount: number;
  onLeaveAnimationCompleted?: () => void;
}

const SkinnerBoxFAB = ({
  className,
  ["aria-label"]: ariaLabel,
  initialDelay = ANIMATION_CONFIG.INITIAL_DELAY,
  onAnimationStart,
  zIndex = 10,
  claimableAmount,
  onLeaveAnimationCompleted,
}: SkinnerBoxFABProps) => {
  const imgStar = getThemeAsset("/skinner-box/star.png");
  const refSpinningNumber = useRef<SpinningNumberRef>(null);
  const refFlyingCoins = useRef<FlyingCoinsRef>(null);
  const fallbackTimerRef = useRef<number | null>(null);
  const isCompletionTriggeredRef = useRef(false);

  // Use custom hook for state management
  const {
    state,
    setState,
    clickLocked,
    setClickLocked,
    guardClick,
    handleClaimClick,
    showClaim,
    isClaimed,
    showSparkles,
    sparklesDirection,
    layerZIndexes,
    isLoading,
  } = useSkinnerBoxState(zIndex, {
    onClaimSuccess: () => {
      // Start fallback timer immediately when claim request succeeds
      const maxAnimationMs = SKINNER_BOX_CONFIG.LEAVE_ANIM_DURATION_MS + 200; // add 200ms buffer

      if (fallbackTimerRef.current) {
        window.clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
      fallbackTimerRef.current = window.setTimeout(() => {
        // If animation completion hasn't been called in time, trigger manually
        if (!isCompletionTriggeredRef.current) {
          isCompletionTriggeredRef.current = true;
          onLeaveAnimationCompleted?.();
        }
        fallbackTimerRef.current = null;
      }, maxAnimationMs);
    },
  });

  const [animationState, setAnimationState] = useState<ANIMATION_STATE>(ANIMATION_STATE.IDLE);
  const [isBonusActivated, setIsBonusActivated] = useState(false);
  const bonusTimerRef = useRef<number | null>(null);
  const { isBlacklisted } = useAuthSession();

  // Handle spinning number completion
  const handleSpinningNumberCompleted = useCallback(() => {
    setState(SKINNER_BOX_STATE.CLAIMABLE);
    setClickLocked(false);
  }, [setState, setClickLocked]);

  // Handle box click with spin or claim logic
  const handleBoxClick = useCallback(() => {
    if (!claimableAmount || guardClick()) return;

    // If claim is shown, clicking the box should claim
    if (showClaim) {
      handleClaimClick();
      return;
    }

    // When clicking on box (showing BONUS), trigger bonus animation immediately
    setIsBonusActivated(true);
    // Clear previous timer if exists
    if (bonusTimerRef.current) {
      clearTimeout(bonusTimerRef.current);
    }
    // Reset after animation duration
    bonusTimerRef.current = window.setTimeout(() => {
      setIsBonusActivated(false);
      bonusTimerRef.current = null;
    }, 1500);

    // Otherwise start spin
    setClickLocked(true);
    refSpinningNumber.current?.startSpin?.(claimableAmount);
  }, [claimableAmount, guardClick, showClaim, handleClaimClick, setClickLocked]);

  const cleanupFallbacks = useCallback(() => {
    if (fallbackTimerRef.current) {
      window.clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
    isCompletionTriggeredRef.current = true;
  }, []);

  const handleAnimationCompleted = useCallback(() => {
    if (state === SKINNER_BOX_STATE.CLAIMED && !isCompletionTriggeredRef.current) {
      isCompletionTriggeredRef.current = true;
      onLeaveAnimationCompleted?.();
      cleanupFallbacks();
    }
  }, [onLeaveAnimationCompleted, state, cleanupFallbacks]);

  // Effects
  useEffect(() => {
    if (state === SKINNER_BOX_STATE.CLAIMED) {
      refSpinningNumber.current?.hideSpin?.();
      refFlyingCoins.current?.start?.();
    }
  }, [state]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setAnimationState(ANIMATION_STATE.ANIMATING);
      onAnimationStart?.();
    }, initialDelay);
    return () => window.clearTimeout(timer);
  }, [initialDelay, onAnimationStart]);

  // Cleanup bonus timer on unmount
  useEffect(() => {
    return () => {
      if (bonusTimerRef.current) {
        clearTimeout(bonusTimerRef.current);
        bonusTimerRef.current = null;
      }
    };
  }, []);

  if (isBlacklisted) {
    return null;
  }

  return (
    <motion.div
      className={cn("relative h-[128px] w-[128px] flex-shrink-0 cursor-pointer", className)}
      style={{ zIndex }}
      aria-label={ariaLabel}
      variants={containerVariants}
      initial={false}
      animate={state}
      onAnimationComplete={handleAnimationCompleted}
      onClick={handleBoxClick}
    >
      {/* Background Components */}
      <SkinnerBoxBackground
        animationState={animationState}
        zIndex={layerZIndexes.background}
        isClicked={clickLocked || state !== SKINNER_BOX_STATE.IDLE}
      />

      {/* Sparkles: controlled play/once */}
      <AnimatePresence>
        {showSparkles ? (
          <SkinnerBoxSparkles
            key={`${sparklesDirection}`}
            imgStar={imgStar}
            play={true}
            isLooping={sparklesDirection === "out"}
            direction={sparklesDirection}
            zIndex={layerZIndexes.sparkle}
            durationMs={
              sparklesDirection === "out"
                ? SKINNER_BOX_CONFIG.SPARKLES_DURATION_MS_OUT
                : SKINNER_BOX_CONFIG.SPARKLES_DURATION_MS_IN
            }
          />
        ) : null}
      </AnimatePresence>

      {/* Main Content */}
      <SkinnerBoxContent zIndex={layerZIndexes.content} />

      {/* Spinning Number */}
      <SpinningNumber
        ref={refSpinningNumber}
        className="absolute left-1/2 top-[63px] -translate-x-1/2 -translate-y-1/2"
        style={{
          containerHeight: 70,
          containerWidth: 200,
          digitClassName: "pt-4",
          digitWidth: 17,
          font: { size: 22, letterSpacing: "-2px" },
          zIndex: layerZIndexes.number,
          circularClipRadius: 35,
        }}
        onComplete={handleSpinningNumberCompleted}
      />

      {/* Actions (BONUS label or Claim button) */}
      <SkinnerBoxActions
        showClaim={showClaim}
        onClaimClick={handleClaimClick}
        isLoading={isLoading}
        zIndex={layerZIndexes.button}
        isBonusActivated={isBonusActivated}
      />

      {/* Coins (Heartbeat or Flying) */}
      <SkinnerBoxCoins
        ref={refFlyingCoins}
        state={state}
        isClaimed={isClaimed}
        zIndex={layerZIndexes.coin}
      />
    </motion.div>
  );
};

export default SkinnerBoxFAB;
